// ColorCategoriesTab.js - Define color categories (White, SingleColor, DoubleColor) and link to price tables
import {
  React,
  useEffect,
  useState,
  useMemo,
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DataGrid,
  authFetch,
  authFetchRequest,
  alertC,
  confirmC
} from './PricingCommon';

const ColorCategoriesTab = () => {
  const [categories, setCategories] = useState([]);
  const [priceTables, setPriceTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [categoriesData, tablesData] = await Promise.all([
        authFetch('/pricing/color-categories'),
        authFetch('/pricing/tables')
      ]);
      setCategories(categoriesData);
      setPriceTables(tablesData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleSave = async (formData) => {
    try {
      let updatedRow;
      if (editData?.id) {
        updatedRow = await authFetchRequest(`/pricing/color-categories/${editData.id}`, formData, 'PUT');
        setCategories(prev => prev.map(row => row.id === editData.id ? updatedRow : row));
        if (selectedRow?.id === editData.id) setSelectedRow(updatedRow);
      } else {
        updatedRow = await authFetchRequest('/pricing/color-categories', formData, 'POST');
        setCategories(prev => [...prev, updatedRow]);
      }
      setOpenDialog(false);
      setEditData(null);

    } catch (err) {
      console.error('Save failed:', err);
      alertC('Failed to save color category');
    }
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    const confirmed = await confirmC('Are you sure you want to delete this color category?');
    if (!confirmed) return;
    try {
      await authFetchRequest(`/pricing/color-categories/${selectedRow.id}`, null, 'DELETE');
      setCategories(prev => prev.filter(row => row.id !== selectedRow.id));
      setSelectedRow(null);

    } catch (err) {
      console.error('Delete failed:', err);
      alertC('Failed to delete color category');
    }
  };

  // Build a map of table IDs to names for display
  const tableNameMap = useMemo(() => {
    const map = {};
    priceTables.forEach(t => { map[t.id] = t.name || t.code; });
    return map;
  }, [priceTables]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    { field: 'code', headerName: 'Code', width: 150 },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'pricing_table_id', headerName: 'Price Table', width: 220, renderCell: (params) => tableNameMap[params.value] || (params.value ? `Table #${params.value}` : '-') },
    { field: 'sort_order', headerName: 'Order', width: 70 },
    { field: 'active', headerName: 'Active', width: 70, renderCell: (params) => params.value ? 'Yes' : 'No' }
  ];

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Color Categories</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={() => { setEditData(null); setOpenDialog(true); }}>
          Add Category
        </Button>
        <Button variant="contained" color="secondary" size="small" onClick={() => { setEditData(selectedRow); setOpenDialog(true); }} disabled={!selectedRow}>
          Edit Category
        </Button>
        <Button variant="outlined" color="error" size="small" onClick={handleDelete} disabled={!selectedRow}>
          Delete Category
        </Button>
      </Stack>

      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <span style={{ fontSize: '0.85em', color: '#555' }}>
          Color categories link color selections to price tables. For example, "White" uses the white price table,
          while "Double Color" (different interior/exterior) uses a higher-priced table.
        </span>
      </Box>

      {loading ? <CircularProgress size={24} /> : (
        <DataGrid
          autoHeight
          rowHeight={30}
          rows={categories}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          onRowClick={(params) => setSelectedRow(params.row)}
          sx={{
            '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#e3f2fd' },
            '& .MuiDataGrid-row': { cursor: 'pointer' }
          }}
          getRowClassName={(params) => params.row.id === selectedRow?.id ? 'Mui-selected' : ''}
        />
      )}

      <ColorCategoryDialog
        open={openDialog}
        data={editData}
        priceTables={priceTables}
        onClose={() => { setOpenDialog(false); setEditData(null); }}
        onSave={handleSave}
      />
    </Box>
  );
};

const ColorCategoryDialog = ({ open, data, priceTables, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', pricing_table_id: '', sort_order: 0, active: true
  });

  useEffect(() => {
    if (data) {
      setFormData({
        code: data.code || '',
        name: data.name || '',
        description: data.description || '',
        pricing_table_id: data.pricing_table_id || '',
        sort_order: data.sort_order || 0,
        active: data.active ?? true
      });
    } else {
      setFormData({ code: '', name: '', description: '', pricing_table_id: '', sort_order: 0, active: true });
    }
  }, [data, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data?.id ? 'Edit Color Category' : 'Add Color Category'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} fullWidth placeholder="e.g., white, single_color, double_color" />
          <TextField label="Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth placeholder="e.g., White (Unpainted)" />
          <TextField label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth multiline rows={2} />
          <FormControl fullWidth>
            <InputLabel>Price Table</InputLabel>
            <Select value={formData.pricing_table_id} onChange={(e) => setFormData({ ...formData, pricing_table_id: e.target.value })} label="Price Table">
              <MenuItem value="">-- None --</MenuItem>
              {priceTables.map(table => (
                <MenuItem key={table.id} value={table.id}>{table.name || table.code}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <TextField label="Sort Order" type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })} sx={{ width: 120 }} />
            <FormControlLabel control={<Checkbox checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />} label="Active" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(formData)}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColorCategoriesTab;
